import { useMemo } from 'react';
import createDictionaryAccessor from '@/creditflowresearch/application/createDictionaryAccessor';
import type {
  IDictionaryBenchmark,
  IDictionaryCouponType,
  IDictionaryCountry,
  IDictionaryCurrency,
  IDictionaryDealStatus,
  IDictionaryRanking,
  IDictionaryIndustry,
  IDictionaryEsgType,
  IDictionaryListing,
  IDictionaryUopType,
  IDictionaryMarket,
  IDictionaryMarketSegment,
  IDictionaryRegistration,
  IDictionarySizeDescription,
} from '@/creditflowresearch/domain/vo/IDictionary';

export interface IUseCorporateBondScreenerDictionariesProps {
  currencies?: IDictionaryCurrency[],
  benchmarks?: IDictionaryBenchmark[],
  couponTypes?: IDictionaryCouponType[],
  rankings?: IDictionaryRanking[],
  countries?: IDictionaryCountry[],
  industries?: IDictionaryIndustry[],
  dealStatuses?: IDictionaryDealStatus[],
  esgTypes?: IDictionaryEsgType[],
  listings?: IDictionaryListing[],
  uopTypes?: IDictionaryUopType[],
  markets?: IDictionaryMarket[],
  marketSegments?: IDictionaryMarketSegment[],
  debutTypes?: { id: string, name: string, priority: number }[],
  products?: { id: string, name: string }[],
  registrations?: IDictionaryRegistration[],
  sizeDescriptions?: IDictionarySizeDescription[],
}

const useCorporateBondScreenerDictionaries = ({
  currencies = [],
  benchmarks = [],
  couponTypes = [],
  rankings = [],
  countries = [],
  industries = [],
  dealStatuses = [],
  esgTypes = [],
  listings = [],
  uopTypes = [],
  markets = [],
  marketSegments = [],
  debutTypes = [],
  products = [],
  registrations = [],
  sizeDescriptions = [],
}: IUseCorporateBondScreenerDictionariesProps) => {
  const industriesAccessor = useMemo(() => createDictionaryAccessor(industries), [industries]);
  const subindustriesAccessor = useMemo(
    () => createDictionaryAccessor(industries.flatMap((industry) => industry.subindustries ?? [])),
    [industries],
  );

  return {
    currencies: createDictionaryAccessor(currencies),
    benchmarks: createDictionaryAccessor(benchmarks),
    couponTypes: createDictionaryAccessor(couponTypes),
    rankings: createDictionaryAccessor(rankings),
    countries: createDictionaryAccessor(countries),
    industries: industriesAccessor,
    subindustries: subindustriesAccessor,
    dealStatuses: createDictionaryAccessor(dealStatuses),
    esgTypes: createDictionaryAccessor(esgTypes),
    listings: createDictionaryAccessor(listings),
    uopTypes: createDictionaryAccessor(uopTypes),
    markets: createDictionaryAccessor(markets),
    marketSegments: createDictionaryAccessor(marketSegments),
    debutTypes: createDictionaryAccessor(debutTypes),
    products: createDictionaryAccessor(products),
    registrations: createDictionaryAccessor(registrations),
    sizeDescriptions: createDictionaryAccessor(sizeDescriptions),
  };
};

export default useCorporateBondScreenerDictionaries;
